import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import type { GateRegularStatus } from './memberAppApi';

/** A PIN the person can still use at the gate. */
export interface PersonLiveInvite {
  access_invite_id: string;
  access_entry_type_id: string | null;
  pin_lifecycle: string;
  valid_from: string | null;
  valid_until: string | null;
  arrived_at: string | null;
}

/** One of the member's people, as saved. */
export interface Person {
  member_contact_id: string;
  display_name: string;
  phone: string | null;
  email: string | null;
  vehicle_reg: string | null;
  /** The kind of visitor they are; every PIN they get follows this entry type. Null is Any type. */
  access_entry_type_id: string | null;
  /** ISO weekdays (1 = Monday ... 7 = Sunday) they visit; each brings a fresh PIN. */
  visit_days: number[];
  is_active: boolean;
  /** When they were archived, and why when it was not the member's choice: ClickOn removed them. */
  archived_at: string | null;
  archived_reason: 'clickon_removed' | null;
  /** What the gate needs to keep them as a regular. */
  national_id: string | null;
  id_type: 'national_id' | 'passport' | 'other' | null;
  gender: 'male' | 'female' | 'other' | null;
  /** Set once they are a regular kept by the gate (OpenItem); its days replace visit_days. */
  gate_regular: { days: number[]; start_date: string; end_date: string; after_hours: boolean; status: GateRegularStatus } | null;
}

/** A person on the Visitor access list: who they are, and where they stand at the gate. */
export interface MemberContact extends Person {
  live_invites: PersonLiveInvite[];
  last_arrived_at: string | null;
  /** When the member last made them a PIN; New PIN lists the most recent first. */
  last_pin_at: string | null;
}

export interface MemberContactPayload {
  display_name: string;
  phone: string | null;
  email: string | null;
  vehicle_reg: string | null;
  /** Null is Any type: each PIN follows the type picked for it. */
  access_entry_type_id: string | null;
}

export type MemberContactUpdatePayload = Partial<MemberContactPayload> & {
  is_active?: boolean;
  visit_days?: number[];
  national_id?: string | null;
  id_type?: Person['id_type'];
  gender?: Person['gender'];
};

/** One PIN in a person's history, with the arrival the gate recorded. */
export interface PersonVisit {
  access_invite_id: string;
  status: string;
  entry_type_name: string | null;
  location_name: string | null;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  arrived_at: string | null;
  left_at: string | null;
  cancelled_at: string | null;
}

/** One page of a person's visits, newest first, with how many they have in all. */
export interface PersonVisitsPage {
  visits: PersonVisit[];
  page: number;
  totalPages: number;
  total: number;
}

export const MEMBER_CONTACTS_QUERY_KEY = ['member-contacts'] as const;

export const PERSON_VISITS_QUERY_KEY = ['member-contact-visits'] as const;

export const personVisitsQueryKey = (memberContactId: string) => [...PERSON_VISITS_QUERY_KEY, memberContactId] as const;

/** How many of a person's visits one request loads; the All visits drawer loads more on request. */
const PERSON_VISITS_LIMIT = 20;

const BASE = '/public/me/member-contacts';

export async function fetchMemberContacts(): Promise<MemberContact[]> {
  const { data } = await tenantApi.get(BASE);
  return unwrapList<MemberContact>(data);
}

export async function createMemberContact(payload: MemberContactPayload): Promise<Person> {
  const { data } = await tenantApi.post(BASE, payload);
  return unwrap<Person>(data);
}

export async function updateMemberContact(
  id: string,
  payload: MemberContactUpdatePayload,
): Promise<Person> {
  const { data } = await tenantApi.patch(`${BASE}/${encodeURIComponent(id)}`, payload);
  return unwrap<Person>(data);
}

export async function fetchPersonVisits(id: string, page: number): Promise<PersonVisitsPage> {
  const { data } = await tenantApi.get<{ data: PersonVisit[]; pagination: Omit<PersonVisitsPage, 'visits'> }>(
    `${BASE}/${encodeURIComponent(id)}/visits`,
    { params: { page, limit: PERSON_VISITS_LIMIT } },
  );
  return { visits: data.data, ...data.pagination };
}

/** One of the member's PINs as a visit, for the gate history drawer. */
export async function fetchMyVisit(accessInviteId: string): Promise<PersonVisit> {
  const { data } = await tenantApi.get(`/public/me/access-invites/${encodeURIComponent(accessInviteId)}/visit`);
  return unwrap<PersonVisit>(data);
}
